import React, { memo, useCallback } from 'react';
import { ResizeCorner } from '@/types';

interface ResizeHandleProps {
  corner: ResizeCorner;
  onResizeStart: (corner: ResizeCorner, e: React.MouseEvent) => void;
  isHovered: boolean;
  onHoverChange: (corner: ResizeCorner | null) => void;
}

const HANDLE_CONFIG: Record<ResizeCorner, {
  position: React.CSSProperties;
  cursor: string;
  isCorner: boolean;
}> = {
  nw: { position: { top: -5, left: -5 }, cursor: 'nwse-resize', isCorner: true },
  n:  { position: { top: -4, left: '50%', transform: 'translateX(-50%)' }, cursor: 'ns-resize', isCorner: false },
  ne: { position: { top: -5, right: -5 }, cursor: 'nesw-resize', isCorner: true },
  w:  { position: { top: '50%', left: -4, transform: 'translateY(-50%)' }, cursor: 'ew-resize', isCorner: false },
  e:  { position: { top: '50%', right: -4, transform: 'translateY(-50%)' }, cursor: 'ew-resize', isCorner: false },
  sw: { position: { bottom: -5, left: -5 }, cursor: 'nesw-resize', isCorner: true },
  s:  { position: { bottom: -4, left: '50%', transform: 'translateX(-50%)' }, cursor: 'ns-resize', isCorner: false },
  se: { position: { bottom: -5, right: -5 }, cursor: 'nwse-resize', isCorner: true },
};

const ResizeHandle: React.FC<ResizeHandleProps> = memo(({
  corner,
  onResizeStart,
  isHovered,
  onHoverChange,
}) => {
  const config = HANDLE_CONFIG[corner];
  const size = config.isCorner ? 10 : 8;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onResizeStart(corner, e);
  }, [corner, onResizeStart]);

  const handleMouseEnter = useCallback(() => {
    onHoverChange(corner);
  }, [corner, onHoverChange]);

  const handleMouseLeave = useCallback(() => {
    onHoverChange(null);
  }, [onHoverChange]);

  const hoverScale = isHovered ? 'scale(1.3)' : 'scale(1)';
  const existingTransform = config.position.transform || '';

  return (
    <div
      onMouseDownCapture={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'absolute',
        ...config.position,
        width: size,
        height: size,
        borderRadius: config.isCorner ? '50%' : 3,
        background: isHovered ? '#ffffff' : 'rgba(255,255,255,0.85)',
        border: '1.5px solid rgba(0,0,0,0.5)',
        cursor: config.cursor,
        zIndex: 100,
        transform: `${existingTransform} ${hoverScale}`.trim(),
        transition: 'transform 0.12s ease, background 0.12s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }}
    />
  );
});

ResizeHandle.displayName = 'ResizeHandle';

export default ResizeHandle;
